// @flow
import Api from "./index.js";
import type {Background} from "./types.js";
import Image from "./image.js";
import type {JSONPage} from "./jsontypes.js";
import {text} from "../xhr.js";
import preprocess from "../pagehandler/preprocess.js";
import type UrlResolver from "../player/urlresolver.js";
import { hasWebPSupport, hasAVIFSupport } from '../environment.js';

const LINK_RE = /<(\w+)[^>]+class="(int|ext)Link"[^>]*>/g;
const PAGE_RE = /data-page="(\d+)"/;
const ID_RE = /data-lookupid="(\w+)"/;
const SLUG_RE = /data-slug="([^"]+)"/;
const TARGET_RE = /target="([^"]+)"/;
const HREF_RE = /(data-)?href="([^"]+)"/;
const PS_HREF_RE = /\shref="https?:\/\/(staging\.|dev\.)?pagestrip\.com\/[\w]{2,5}\/([\w-]+)\/(\w+)\/([\w-]+)\/(\?[\w=&;]*in=(\w+)[\w=&;]*)?"/;

export default class Page {
	_api: Api;
	_promise: Promise<Page>;

	url: string;
	baseUrl: string;
	type: "flow" | "normal" = "normal";
	fit: "width" | "height" | "both" = "width";
	thumb: ?Image;
	size: [number, number] = [1024, 768];
	maxWidth: ?number;
	background: Background = {};
	relImgSize: boolean = true;
	useRelativeLayout: boolean = true;
	internalLinksRetainHierarchy = false;

	loaded: boolean = false;
	content: ?string;

	constructor(api: Api, url: string, baseUrl: string, page?: JSONPage, options?: any) {
		this._api = api;
		this.url = `${baseUrl}${url}`;
		this.baseUrl = baseUrl;
		this.type = page ? "normal" : "flow";
		this.internalLinksRetainHierarchy = options ? !!options.internalLinksRetainHierarchy : false
		if (page) {
			this.size = page.size;
			if (page.maxWidth) {
				this.maxWidth = page.maxWidth;
			}
			this.thumb = new Image(page.thumb, baseUrl);
			if (page.backgroundColor) {
				this.background.color = page.backgroundColor;
			}
			if (page.backgroundImage) {
				this.background.image = new Image({
					url: page.backgroundImage,
					res: Array.isArray(page.backgroundScales) ? page.backgroundScales : [1, 2],
					size: page.backgroundAssetSize ? page.backgroundAssetSize : page.size
				}, baseUrl, hasWebPSupport && options && options.hasWebP, hasAVIFSupport && options && options.hasAVIF);
			}

			if (page.backgroundImageFixed) {
				this.background.fixed = true;
			}

			if (page.backgroundImagePosition) {
				this.background.pos = page.backgroundImagePosition;
			}

			if (page.backgroundScrollScale) {
				this.background.scrollScale = page.backgroundScrollScale;
			}
	  
			this.background.limitWidth = !!page.backgroundLimitWidth;
			this.background.size = page.backgroundImageSize;
			this.background.repeat = page.backgroundImageRepeat || "no-repeat";

			this.relImgSize = page.relImgSize;
			this.useRelativeLayout = page.useRelativeLayout;
			let {fit} = page;
			// XXX: the editor should *really* *really* provide this!
			if (!fit) {
				const {size} = page;
				fit = size[1] > size[0]
					? "width" // long page
					: "both";
			}
			this.fit = fit;
		}
	}

	load(stylePrefix?: string): Promise<Page> {
		if (this._promise) {
			return this._promise;
		}
		const promise = this._promise = text(this.url)
		.then((html: string) =>
			processHTML(this._api, preprocess(html, this.baseUrl, stylePrefix, this._api.options.headingBaseLevel)))
		.then((html: string) => {
			this.loaded = true;
			this.content = html;
			return this;
		});
		return promise;
	}

	// This will primarily create real links, correct with live query string
	// params for external and internal links. It will also make sure to wrap
	// links correctly in a `a` element if they are not one already.
	processHTML(url: UrlResolver) {
		let html = this.content;
		if (!html) { return ""; }
		let match;
		while ((match = LINK_RE.exec(html))) {
			const link = match[0];
			const elem = match[1];
			const type = match[2];

			let href, needHref = true;
			let psLink;
			if (type === "ext") {
				const _href = link.match(HREF_RE);
				needHref = Boolean(_href[1]);
				href = _href[2];
				psLink = link.match(PS_HREF_RE)
			} else {
				const _id = link.match(ID_RE);
				const id = _id && _id[1];
				const _page = link.match(PAGE_RE);
				const page = _page && _page[1];
				const _slug = link.match(SLUG_RE);
				const slug = _slug && _slug[1];

				href = hrefForLink(url, id, page, slug, undefined, this.internalLinksRetainHierarchy);
			}

			if (elem !== "a") {
				const _target = link.match(TARGET_RE);
				const target = _target ? ` ${_target[0]}` : "";
				// we need to wrap the element in a real `a`
				const idx = match.index;
				// XXX: well fuck, this still can fail horribly if we have more
				// nesting, since `endTag` is not guaranteed to actually match *the*
				// tag. Well so much for using regexp instead of a real
				// parser/tokenizer. But then again, this should only fail for the
				// legacy cases of non-`a` tags, which the editor should hopefully not
				// generate anymore.
				const endTag = `</${elem}>`;
				const toIdx = html.indexOf(endTag, idx) + endTag.length;
				const toWrap = html.substring(idx, toIdx);
				const inserting = `<a href="${href}"${target} class="${type}Link">${toWrap}</a>`;
				html = `${html.substring(0, idx)}${inserting}${html.substring(toIdx)}`;
				// we need to update the regexp index, otherwise it will lead to an
				// infinite loop
				LINK_RE.lastIndex += inserting.length;
			} else if (needHref) {
				const idx = match.index + 1 + elem.length;
				html = `${html.substring(0, idx)} href="${href}"${html.substring(idx)}`;
			} else if(psLink) {
				const idx = match.index + 1 + elem.length;
				href = hrefForLink(url, psLink[3], null, psLink[4], psLink.length > 6 ? psLink[6] : false, this.internalLinksRetainHierarchy)
				html = `${html.substring(0, idx)} href="${href}"${html.substring(idx)}`;
			}
		}
		return html;
	}
}

function hrefForLink(url, id, page, slug, context, retainHierarchy) {
	const to = {};
	if (id) {
		 //remove last (current) item since it is a story and not really important for the hierarchy,
		 // unless it is important...
		const hierarchy = url.hierarchy.slice(0, retainHierarchy ? undefined : -1);
		const index = hierarchy.lastIndexOf(id);
		if (index !== -1) {
			if (retainHierarchy) {
				// if we retain the hierarchy item, make sure it's in the list only once
				let prev = 0;
				while ((prev = hierarchy.indexOf(id)) >= 0 && prev !== index) {
					hierarchy.splice(prev, 1);
				}
			} else {
				hierarchy.splice(index, 1);
			}
		}
		hierarchy.push(id);
		to.hierarchy = hierarchy;
	}
	if (page || context) {
		to.query = {}
		if (page) to.query['page'] = page;
		if (context) to.query['in'] = context;
	}
	if (slug) {
		to.slug = slug;
	}
	return url.create(to);
}

// this will resolve internal links using the api
function processHTML(api: Api, html: string): Promise<string> {
	const links = html.match(LINK_RE);
	if (!links) {
		return Promise.resolve(html);
	}
	return Promise.all(links.map(link => {
		const _id = link.match(ID_RE);
		const id = _id && _id[1];
		const _slug = link.match(SLUG_RE);
		const slug = _slug && _slug[1];
		return id && slug || id && api.resolve(id).catch(e => true);
	})).then(() => html);
}



// WEBPACK FOOTER //
// ./lib/api/page.js